import React, { Suspense, useCallback, useEffect, useRef, useState } from "react"
import Box from "@mui/material/Box"
import IconButton from "@mui/material/IconButton"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import CircularProgress from "@mui/material/CircularProgress"
import CloseIcon from "@mui/icons-material/Close"
import MinimizeIcon from "@mui/icons-material/Remove"
import MaximizeIcon from "@mui/icons-material/CropSquare"
import { InWindowProvider, useSpotlight } from "./SpotlightContext"
import { WINDOW_DEFS } from "./SpotlightSearch"
import TabbedWorkspace from "../workspace/TabbedWorkspace"
import { useDesktopWorkspace } from "../desktop/useDesktopWorkspace"
import { useAppearance } from "../appearance/AppearanceContext"
import ErrorBoundary from "../../shared/ErrorBoundary"

// ── Constants ──────────────────────────────────────────────────────────────────

const TITLE_H = 40
const MIN_W   = 300
const MIN_H   = 180
const MARGIN  = 10
const NAV     = 64
const SNAP_DIST = 30
const EDGE    = 16

// ── Snap helpers (same as TerminalWindow) ──────────────────────────────────────

function snapZoneAt(mx, my) {
  const ww = window.innerWidth
  const wh = window.innerHeight
  const nearLeft  = mx < NAV + SNAP_DIST
  const nearRight = mx > ww - SNAP_DIST
  const nearTop   = my < SNAP_DIST
  const nearBot   = my > wh - SNAP_DIST

  if (nearLeft && nearTop)  return "tl"
  if (nearRight && nearTop) return "tr"
  if (nearLeft && nearBot)  return "bl"
  if (nearRight && nearBot) return "br"
  if (nearLeft)             return "l"
  if (nearRight)            return "r"
  if (nearTop)              return "full"
  return null
}

function snapRect(zone) {
  const ww   = window.innerWidth
  const wh   = window.innerHeight
  const half  = Math.floor((ww - NAV) / 2)
  const halfH = Math.floor(wh / 2)
  switch (zone) {
    case "tl":   return { x: NAV + MARGIN, y: MARGIN,    w: half - MARGIN,         h: halfH - MARGIN }
    case "tr":   return { x: NAV + half,   y: MARGIN,    w: ww - NAV - half - MARGIN, h: halfH - MARGIN }
    case "bl":   return { x: NAV + MARGIN, y: halfH,     w: half - MARGIN,         h: halfH - MARGIN }
    case "br":   return { x: NAV + half,   y: halfH,     w: ww - NAV - half - MARGIN, h: halfH - MARGIN }
    case "l":    return { x: NAV,          y: 0,         w: half,                  h: wh }
    case "r":    return { x: NAV + half,   y: 0,         w: ww - NAV - half,       h: wh }
    case "full": return { x: NAV,          y: 0,         w: ww - NAV,              h: wh }
    default:     return null
  }
}

function snapBorderStyle(zone) {
  if (!zone) return {}
  const glow = "rgba(100,100,200,0.55)"
  const pinT = ["tl", "tr", "full"].includes(zone)
  const pinB = ["bl", "br"].includes(zone)
  const pinL = ["tl", "bl", "l", "full"].includes(zone)
  const pinR = ["tr", "br", "r", "full"].includes(zone)
  const tl = (pinT && pinL) ? 0 : 6
  const tr = (pinT && pinR) ? 0 : 6
  const br = (pinB && pinR) ? 0 : 6
  const bl = (pinB && pinL) ? 0 : 6
  return {
    borderRadius: `${tl}px ${tr}px ${br}px ${bl}px`,
    ...(pinT && { borderTop:    `2px solid ${glow}` }),
    ...(pinB && { borderBottom: `2px solid ${glow}` }),
    ...(pinL && { borderLeft:   `2px solid ${glow}` }),
    ...(pinR && { borderRight:  `2px solid ${glow}` }),
  }
}

function resizeCursorStyle(edge) {
  const map = { n:"n-resize", s:"s-resize", e:"e-resize", w:"w-resize",
    ne:"ne-resize", nw:"nw-resize", se:"se-resize", sw:"sw-resize" }
  return map[edge] ?? "default"
}

function getEdge(e, el) {
  const r = el.getBoundingClientRect()
  const x = e.clientX - r.left
  const y = e.clientY - r.top
  const w = r.width, h = r.height
  const onN = y < EDGE, onS = y > h - EDGE
  const onW = x < EDGE, onE = x > w - EDGE
  if (onN && onW) return "nw"
  if (onN && onE) return "ne"
  if (onS && onW) return "sw"
  if (onS && onE) return "se"
  if (onN) return "n"
  if (onS) return "s"
  if (onW) return "w"
  if (onE) return "e"
  return null
}

// ── LoadingFallback ────────────────────────────────────────────────────────────

function WindowLoader({ color }) {
  return (
    <Box sx={{
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      backgroundColor: "var(--app-bg)",
    }}>
      <CircularProgress size={24} sx={{ color: color ?? "#6464c8" }} />
    </Box>
  )
}

// ── FloatingWindow ─────────────────────────────────────────────────────────────

export default function FloatingWindow({ win }) {
  const { closeWindow, focusWindow, minimizeWindow, updateGeom, visibleWindows } = useSpotlight()
  const { reduceAnimations } = useAppearance()
  const { prefs, adaptiveSlowdown } = useDesktopWorkspace()
  const def = WINDOW_DEFS.find((d) => d.id === win.typeId)
  if (!def) return null

  const { color = "#6464c8", label, component: Comp } = def

  const winRef      = useRef(null)
  const dragRef     = useRef({ on: false, sx: 0, sy: 0, ox: 0, oy: 0 })
  const resizeRef   = useRef({ on: false, edge: null, sx: 0, sy: 0, ox: 0, oy: 0, ow: 0, oh: 0 })
  const geomRef     = useRef({ x: win.x, y: win.y, w: win.w, h: win.h })
  const rafRef      = useRef(null)

  const [geom, setGeomState]   = useState({ x: win.x, y: win.y, w: win.w, h: win.h })
  const [snap, setSnap]        = useState(win.snap ?? null)
  const [ghost, setGhost]      = useState(null)
  const [isMoving, setIsMoving] = useState(false)
  const [cursor, setCursor]    = useState("default")
  const [collapseOrigin, setCollapseOrigin] = useState(win.origin)
  const minimized = win.minimized
  const activeZ = Math.max(-1, ...visibleWindows.filter((item) => !item.minimized).map((item) => item.zIndex))
  const active = !minimized && win.zIndex === activeZ
  const motion = prefs.windowEffects && prefs.visualPreset !== "minimal" && !prefs.performanceMode && !adaptiveSlowdown && !reduceAnimations
  const cinematic = motion && prefs.visualPreset === "cinematic"

  useEffect(() => {
    const next = { x: win.x, y: win.y, w: win.w, h: win.h }
    geomRef.current = next
    setGeomState(next)
    setSnap(win.snap ?? null)
  }, [win.x, win.y, win.w, win.h, win.snap])

  // ── Drag ───────────────────────────────────────────────────────────────────

  const onTitleDown = useCallback((e) => {
    if (e.target.closest("button")) return
    if (e.button !== 0) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    focusWindow(win.id)

    const el = winRef.current
    let ox = geomRef.current.x, oy = geomRef.current.y
    if (snap) {
      if (el) { const r = el.getBoundingClientRect(); ox = r.left; oy = r.top }
      setSnap(null)
      if (el) { el.style.left = ox + "px"; el.style.top = oy + "px"; el.style.right = ""; el.style.bottom = "" }
      geomRef.current = { ...geomRef.current, x: ox, y: oy }
    }
    dragRef.current = { on: true, sx: e.clientX, sy: e.clientY, ox, oy }
    setIsMoving(true)
  }, [snap, win.id, focusWindow])

  const onTitleMove = useCallback((e) => {
    if (!dragRef.current.on) return
    const { sx, sy, ox, oy } = dragRef.current
    const nx = Math.max(0, Math.min(window.innerWidth - geomRef.current.w, ox + e.clientX - sx))
    const ny = Math.max(0, Math.min(window.innerHeight - TITLE_H, oy + e.clientY - sy))
    const el = winRef.current
    if (el) { el.style.left = nx + "px"; el.style.top = ny + "px" }
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      setGhost((() => { const z = snapZoneAt(e.clientX, e.clientY); return z ? snapRect(z) : null })())
    })
  }, [])

  const onTitleUp = useCallback((e) => {
    if (!dragRef.current.on) return
    dragRef.current.on = false
    e.currentTarget.releasePointerCapture(e.pointerId)
    setIsMoving(false)
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }

    const zone = snapZoneAt(e.clientX, e.clientY)
    if (zone) {
      const r = snapRect(zone)
      if (winRef.current) winRef.current.style.cssText = ""
      geomRef.current = { x: r.x, y: r.y, w: r.w, h: r.h }
      setGeomState({ ...geomRef.current })
      setSnap(zone)
    } else {
      const el = winRef.current
      if (el) {
        const r = el.getBoundingClientRect()
        geomRef.current = { ...geomRef.current, x: r.left, y: r.top }
        el.style.cssText = ""
        setGeomState({ ...geomRef.current })
      }
    }
    setGhost(null)
  }, [])

  // ── Window-level resize ────────────────────────────────────────────────────

  const onWinMove = useCallback((e) => {
    if (resizeRef.current.on) {
      const { edge, sx, sy, ox, oy, ow, oh } = resizeRef.current
      const dx = e.clientX - sx, dy = e.clientY - sy
      let x = ox, y = oy, w = ow, h = oh
      if (edge.includes("e")) w = Math.max(MIN_W, ow + dx)
      if (edge.includes("s")) h = Math.max(MIN_H, oh + dy)
      if (edge.includes("w")) { const nw = Math.max(MIN_W, ow - dx); x = ox + ow - nw; w = nw }
      if (edge.includes("n")) { const nh = Math.max(MIN_H, oh - dy); y = oy + oh - nh; h = nh }
      const el = winRef.current
      if (el) { el.style.left = x + "px"; el.style.top = y + "px"; el.style.width = w + "px"; el.style.height = h + "px"; el.style.right = ""; el.style.bottom = "" }
      return
    }
    if (!dragRef.current.on && winRef.current && !minimized) {
      const edge = getEdge(e, winRef.current)
      setCursor(edge ? resizeCursorStyle(edge) : "default")
    }
  }, [minimized])

  const onWinDown = useCallback((e) => {
    if (!winRef.current || minimized) return
    const edge = getEdge(e, winRef.current)
    if (!edge) return
    if (e.target.closest("[data-title-bar]")) return
    e.preventDefault(); e.stopPropagation()
    focusWindow(win.id)
    winRef.current.setPointerCapture(e.pointerId)
    let g = geomRef.current
    if (snap) {
      const r = winRef.current.getBoundingClientRect()
      g = { x: r.left, y: r.top, w: r.width, h: r.height }
      geomRef.current = g
      setSnap(null)
    }
    resizeRef.current = { on: true, edge, sx: e.clientX, sy: e.clientY, ox: g.x, oy: g.y, ow: g.w, oh: g.h }
    setIsMoving(true)
  }, [snap, minimized, win.id, focusWindow])

  const onWinUp = useCallback((e) => {
    if (!resizeRef.current.on) return
    resizeRef.current.on = false
    try { winRef.current?.releasePointerCapture(e.pointerId) } catch {}
    setIsMoving(false)
    setCursor("default")
    const el = winRef.current
    if (el) {
      const r = el.getBoundingClientRect()
      geomRef.current = { x: r.left, y: r.top, w: r.width, h: r.height }
      el.style.cssText = ""
      setGeomState({ ...geomRef.current })
    }
  }, [])

  // ── Window style ───────────────────────────────────────────────────────────

  const h = minimized ? TITLE_H : geom.h
  const animationOrigin = collapseOrigin ?? win.origin ?? { x: window.innerWidth / 2, y: window.innerHeight - 54 }
  const originDx = animationOrigin.x - (geom.x + geom.w / 2)
  const originDy = animationOrigin.y - (geom.y + geom.h / 2)
  const originLocalX = Math.max(0, Math.min(geom.w, animationOrigin.x - geom.x))
  const originLocalY = Math.max(0, Math.min(geom.h, animationOrigin.y - geom.y))
  const windowAnimation = !motion ? "none" : minimized
    ? `${cinematic ? "holos-window-collapse" : "holos-window-collapse-lite"} .2s cubic-bezier(.4,0,1,1) forwards`
    : cinematic
      ? "holos-window-black-hole .38s cubic-bezier(.16,1,.3,1) both"
      : "holos-window-open-lite .22s cubic-bezier(.2,.8,.2,1) both"
  const wStyle = (() => {
    if (!snap) return { left: geom.x, top: geom.y, width: geom.w, height: h }
    switch (snap) {
      case "tl":   return { left: NAV + MARGIN,  top: MARGIN,    width: geom.w, height: h }
      case "tr":   return { right: MARGIN,        top: MARGIN,    width: geom.w, height: h }
      case "bl":   return { left: NAV + MARGIN,  bottom: MARGIN, width: geom.w, height: h }
      case "br":   return { right: MARGIN,        bottom: MARGIN, width: geom.w, height: h }
      case "l":    return { left: NAV, top: 0, width: geom.w, height: minimized ? TITLE_H : "100vh" }
      case "r":    return { right: 0,  top: 0, width: geom.w, height: minimized ? TITLE_H : "100vh" }
      case "full": return { left: NAV, top: 0, right: 0,      height: minimized ? TITLE_H : "100vh" }
      default:     return { left: geom.x, top: geom.y, width: geom.w, height: h }
    }
  })()

  return (
    <>
      {ghost && (
        <Box sx={{
          position: "fixed", zIndex: win.zIndex - 1,
          left: ghost.x, top: ghost.y, width: ghost.w, height: ghost.h,
          background: `color-mix(in srgb, ${color} 7.1%, transparent)`,
          border: `2px dashed color-mix(in srgb, ${color} 33.3%, transparent)`,
          borderRadius: 2,
          pointerEvents: "none",
          backdropFilter: prefs.performanceMode || !cinematic ? "none" : "blur(4px)",
          boxShadow: motion ? `inset 0 0 40px color-mix(in srgb, ${color} 9.4%, transparent),0 0 34px color-mix(in srgb, ${color} 12.5%, transparent)` : "none",
          backgroundImage: cinematic ? `linear-gradient(color-mix(in srgb, ${color} 9.4%, transparent) 1px,transparent 1px),linear-gradient(90deg,color-mix(in srgb, ${color} 9.4%, transparent) 1px,transparent 1px)` : "none",
          backgroundSize: "32px 32px",
          animation: motion ? "holos-snap-preview .16s cubic-bezier(.16,1,.3,1) both" : "none",
          "@keyframes holos-snap-preview": { from: { opacity: 0, transform: "scale(.97)" }, to: { opacity: 1, transform: "scale(1)" } },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" },
        }} />
      )}

      <Box
        ref={winRef}
        onPointerMove={onWinMove}
        onPointerDown={onWinDown}
        onPointerUp={onWinUp}
        onClick={() => focusWindow(win.id)}
        sx={{
          position: "fixed",
          zIndex: win.zIndex,
          ...wStyle,
          ...snapBorderStyle(snap),
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          border: snap ? undefined : `1px solid rgba(var(--app-fg-rgb),0.08)`,
          boxShadow: active ? `0 28px 76px rgba(var(--app-shadow-rgb),.82), 0 0 0 1px color-mix(in srgb, ${color} 14.1%, transparent), 0 0 46px color-mix(in srgb, ${color} 9.4%, transparent)` : "0 18px 44px rgba(var(--app-shadow-rgb),.64)",
          backgroundColor: `rgba(8,8,15,${prefs.windowOpacity})`,
          backdropFilter: prefs.performanceMode || adaptiveSlowdown || prefs.visualPreset !== "cinematic" ? "none" : "blur(12px)",
          cursor,
          borderRadius: snap ? undefined : `${prefs.windowRadius}px`,
          opacity: minimized && !motion ? 0 : active ? 1 : .94,
          "--holos-origin-dx": `${originDx}px`,
          "--holos-origin-dy": `${originDy}px`,
          "--holos-origin-open-dx": `${originDx * .08}px`,
          "--holos-origin-open-dy": `${originDy * .08}px`,
          "--holos-origin-x": `${originLocalX}px`,
          "--holos-origin-y": `${originLocalY}px`,
          pointerEvents: minimized ? "none" : "auto",
          transition: isMoving ? "none" : "box-shadow .2s, border-radius .15s, opacity .18s",
          animation: windowAnimation,
          "@keyframes holos-window-open": {
            from: { opacity: 0, transform: "translate3d(var(--holos-origin-open-dx),var(--holos-origin-open-dy),0) scale(.96)", filter: "blur(3px)" },
            to: { opacity: 1, transform: "scale(1) translateY(0)", filter: "blur(0)" },
          },
          "@keyframes holos-window-open-lite": {
            from: { opacity: 0, transform: "translate3d(var(--holos-origin-open-dx),var(--holos-origin-open-dy),0) scale(.97)" },
            to: { opacity: 1, transform: "translate3d(0,0,0) scale(1)" },
          },
          "@keyframes holos-window-black-hole": {
            from: { opacity: 0, transform: "translate3d(var(--holos-origin-dx),var(--holos-origin-dy),0) scale(.04) rotate(-4deg)", clipPath: "circle(0 at var(--holos-origin-x) var(--holos-origin-y))", filter: "blur(12px) brightness(1.8)" },
            "55%": { opacity: 1, clipPath: "circle(72% at var(--holos-origin-x) var(--holos-origin-y))", filter: "blur(1px) brightness(1.15)" },
            to: { opacity: 1, transform: "translate3d(0,0,0) scale(1) rotate(0)", clipPath: "circle(160% at var(--holos-origin-x) var(--holos-origin-y))", filter: "none" },
          },
          "@keyframes holos-window-collapse": {
            from: { opacity: 1, transform: "translate3d(0,0,0) scale(1)" },
            to: { opacity: 0, transform: "translate3d(var(--holos-origin-dx),var(--holos-origin-dy),0) scale(.06)", filter: "blur(6px)" },
          },
          "@keyframes holos-window-collapse-lite": {
            from: { opacity: 1, transform: "translate3d(0,0,0) scale(1)" },
            to: { opacity: 0, transform: "translate3d(var(--holos-origin-dx),var(--holos-origin-dy),0) scale(.06)" },
          },
          "&::before": cinematic ? { content: '""', position: "absolute", inset: 0, zIndex: 5, pointerEvents: "none", borderRadius: "inherit", boxShadow: `inset 0 0 32px color-mix(in srgb, ${color} 9.4%, transparent)`, background: `radial-gradient(circle at ${originLocalX}px ${originLocalY}px,color-mix(in srgb, ${color} 9.4%, transparent),transparent 28%)`, opacity: .8 } : undefined,
          "@media (prefers-reduced-motion: reduce)": { animation: "none", transform: "none", filter: "none", clipPath: "none", transition: "none" },
          userSelect: "none",
        }}
      >
        {/* Title bar */}
        <Box
          data-title-bar
          onPointerDown={onTitleDown}
          onPointerMove={onTitleMove}
          onPointerUp={onTitleUp}
          sx={{
            height: TITLE_H,
            px: 1.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "var(--app-bg-alt)",
            borderBottom: `1px solid rgba(var(--app-fg-rgb),0.06)`,
            cursor: isMoving ? "grabbing" : "grab",
            flexShrink: 0,
            touchAction: "none",
          }}
        >
          <Box sx={{
            width: 8, height: 8, borderRadius: "50%",
            backgroundColor: color,
            boxShadow: `0 0 8px color-mix(in srgb, ${color} 53.3%, transparent)`,
            flexShrink: 0,
          }} />
          <Typography sx={{
            fontFamily: "var(--app-font-mono)",
            fontSize: "0.7rem", fontWeight: 700,
            letterSpacing: "0.1em",
            color,
            flexShrink: 0,
            userSelect: "none",
          }}>
            {label}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <Tooltip title={minimized ? "Restore" : "Minimize"} placement="top">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                const target = document.querySelector(`[data-holos-window-id="${win.typeId}"]`)?.getBoundingClientRect()
                if (target) setCollapseOrigin({ x: target.left + target.width / 2, y: target.top + target.height / 2 })
                requestAnimationFrame(() => minimizeWindow(win.id))
              }}
              sx={{ color: "rgba(var(--app-fg-rgb),0.28)", p: 0.5, "&:hover": { color: "rgba(var(--app-fg-rgb),0.7)" } }}
            >
              {minimized
                ? <MaximizeIcon sx={{ fontSize: 13 }} />
                : <MinimizeIcon sx={{ fontSize: 13 }} />
              }
            </IconButton>
          </Tooltip>

          <Tooltip title="Close" placement="top">
            <IconButton
              size="small"
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); closeWindow(win.id) }}
              sx={{ color: "rgba(var(--app-fg-rgb),0.28)", p: 0.5, "&:hover": { color: "var(--app-error)" } }}
            >
              <CloseIcon sx={{ fontSize: 13 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Content area — override 100vh on page roots */}
        <Box sx={{
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
          display: minimized ? "none" : "flex",
          flexDirection: "column",
          userSelect: "text",
          "& > *": { height: "100% !important", minHeight: "0 !important" },
        }}>
          <InWindowProvider>
            <ErrorBoundary fallback={<Box sx={{ flex: 1, display: "grid", placeItems: "center", color: "#f87171", fontSize: "0.72rem" }}>Dieses Fenster ist abgestürzt. Schließe es und öffne die App erneut.</Box>}>
              <Suspense fallback={<WindowLoader color={color} />}>
                {def.tabs ? <TabbedWorkspace tabs={def.tabs} color={color} /> : <Comp windowPayload={win.payload} windowMinimized={minimized} />}
              </Suspense>
            </ErrorBoundary>
          </InWindowProvider>
        </Box>
      </Box>
    </>
  )
}
